// ============================================================
// INSPECT - what is under the cursor, in words.
//
// A GM who set a material's hardness, transparency and permeability a
// fortnight ago had no way to ask what they chose short of opening Build
// and finding it in the palette. This answers that, and then the more
// useful question underneath it: what does this thing DO?
//
// Two layers, because they are wanted at different times. The first says
// what a thing is and what that means in play. The second says what it
// means for the shot being lined up, which depends on WHO is being shot
// at - cover is a fraction of the target, so the honest answer changes
// with their size.
//
// Pure. Zero React dependency.
// ============================================================
import { solidBounds } from './solids';
import { overlapAt } from './overlap';
import { surfaceAt } from './surfaceEffects';
import { zoneCellKey } from './zones';
import { allSurfaceTraits } from './surfaceTraits';
import { decodeCond } from '../conditions';
import type { Solid, Vec3 } from './types';
import type { Zone } from './zones';
import type { TerrainRules } from './traitRules';

const PERMEABILITY_WORDS = ['solid', 'dense', 'open'] as const;
const TRANSPARENCY_WORDS = ['opaque', 'translucent', 'transparent'] as const;

export interface InspectLine {
  /** A short heading - the material or surface name. */
  title: string;
  /** What it is: the properties a GM set. */
  facts: string[];
  /** What that means in play. */
  meaning: string[];
  /**
   * The deeper structural facts, behind the detail switch.
   *
   * DELIBERATELY NOTHING ABOUT A TARGET. The readout used to name the
   * cover this would give the current one, which goes stale the moment
   * anybody moves - the reading would still be on screen, and wrong. What
   * is here cannot go out of date, because it describes only the thing
   * under the cursor.
   */
  detail: string[];
}

/** Nothing under the cursor - a shared constant, for the memo that holds it. */
export const NOTHING_HERE: InspectLine[] = [];

/**
 * The tallest thing standing over this point, if any.
 *
 * The TALLEST rather than the first: a doorstep and the wall behind it
 * both contain the point, and the wall is what a person means.
 */
function solidAt(at: Vec3, solids: Solid[], includeHidden: boolean): Solid | null {
  return solids
    .filter((solid) => (includeHidden || !solid.hidden) && overPoint(at, solidBounds(solid)))
    .reduce<Solid | null>(
      (best, solid) => (!best || solidBounds(solid).max.y > solidBounds(best).max.y ? solid : best),
      null,
    );
}

/** Is the point inside this box's footprint (edges included)? */
function overPoint(at: Vec3, box: { min: Vec3; max: Vec3 }): boolean {
  return !(at.x < box.min.x || at.x > box.max.x || at.z < box.min.z || at.z > box.max.z);
}

export interface InspectOptions {
  solids: Solid[];
  zones?: Zone[];
  cellSize?: number;
  includeHidden?: boolean;
  /** Whether to include the deeper structural facts. */
  detail?: boolean;
  /** Rules used to say whether a surface would catch THIS combatant. */
  rules?: TerrainRules;
}

const ON_FOOT: TerrainRules = { ignoresTerrain: false, ignoresGoing: false, maxStep: 0.6, canBurrow: false };

/**
 * The readout is PROSE, and prose starts with a capital.
 *
 * It was assembled straight out of the data - "impassable",
 * "Pen:movement=-15", "grants fearRating" - which is what the fields are
 * called, not what they mean. A GM reading the map at speed should not
 * have to translate from the schema.
 */
function sentence(text: string): string {
  return text.length ? text[0].toUpperCase() + text.slice(1) : text;
}

/** Cover levels are stored camelCase; nobody says "threeQuarter". */
const COVER_WORDS: Record<string, string> = {
  none: 'none',
  low: 'low',
  half: 'half',
  threeQuarter: 'three-quarter',
  full: 'full',
};

/** Whose feet a surface reaches, in the words the editor uses. */
const REACH_WORDS: Record<string, string> = {
  flying: 'flyers',
  hovering: 'hoverers',
  burrowing: 'burrowers',
};

/** What movement makes of this ground. */
const TERRAIN_WORDS: Record<string, string> = {
  normal: 'open ground',
  difficult: 'rough going - double cost',
  dangerous: 'dangerous ground',
  impassable: 'impassable',
};

/** A trait field as the trait picker names it - `fearRating` is not a word. */
const TRAIT_LABELS: Record<string, string> = Object.fromEntries(
  allSurfaceTraits().map((trait) => [trait.field, trait.label]),
);

/**
 * Describe what is at this ground point.
 *
 * Returns one entry per thing found - a structure and the ground it
 * stands on are two different answers and a person wants both.
 */
export function inspectAt(at: Vec3, options: InspectOptions): InspectLine[] {
  const out = [...structureLines(at, options), ...groundLines(at, options)];
  if (!out.length) out.push(...insideLines(at, options));
  return out.length ? out : NOTHING_HERE;
}

/** What each transparency means for a shot, by level; anything above 1 lets sight through. */
const SIGHT_MEANING = ['Blocks sight', 'Grants full cover, sight passes'];

/** What each permeability means for a walker, by level; anything above 1 is crossed freely. */
const CROSSING_MEANING = ['Cannot be walked through', 'Crossed at double cost'];

/** The structure standing over the point, if there is one worth the name. */
function structureLines(at: Vec3, { solids, includeHidden = true, detail = false }: InspectOptions): InspectLine[] {
  const solid = solidAt(at, solids, includeHidden);
  if (!solid || !(solid.height > 0.1)) return [];
  const box = solidBounds(solid);
  const ap = solid.cover?.ap ?? 0;
  const permeability = solid.permeability ?? 0;
  const transparency = solid.transparency ?? 0;

  const facts = [
    `${solid.height.toFixed(1)} m tall · top at ${box.max.y.toFixed(1)} m`,
    sentence(`${TRANSPARENCY_WORDS[transparency]} · ${PERMEABILITY_WORDS[permeability]}`),
  ];
  if (ap > 0) facts.unshift(`Hardness ${ap}`);

  const meaning = [
    ap > 0 ? `Cover AP ${ap}` : 'No cover',
    SIGHT_MEANING[transparency] ?? 'Sight passes',
    CROSSING_MEANING[permeability] ?? 'Crossed freely',
  ];

  const deep = detail ? structureDetail(solid, transparency, permeability) : [];
  return [{ title: solid.label || 'Structure', facts, meaning, detail: deep }];
}

/** The structure's structural facts, behind the detail switch. */
function structureDetail(solid: Solid, transparency: number, permeability: number): string[] {
  const box = solidBounds(solid);
  return [
    `Base ${box.min.y.toFixed(1)} m → top ${box.max.y.toFixed(1)} m`,
    `Transparency ${transparency} · permeability ${permeability}`,
    `Movement treats it as ${TERRAIN_WORDS[solid.terrain] ?? solid.terrain}`,
    solid.hidden ? 'GM only - players cannot see it' : 'Visible to players',
  ];
}

/** The painted zones whose cell holds the point, in paint order, hidden ones only when allowed. */
function zonesUnder(at: Vec3, { zones, cellSize = 1, includeHidden = true }: InspectOptions): Zone[] {
  const size = cellSize > 0 ? cellSize : 1;
  const cell = zoneCellKey(Math.floor(at.x / size), Math.floor(at.z / size));
  return (zones ?? []).filter((z) => z.cells?.includes(cell) && (includeHidden || !z.hidden));
}

/**
 * The ground itself is a separate answer, and often the interesting one.
 *
 * FOUND BY CELL, NOT BY LABEL. `surfaceAt` answers what the ground DOES,
 * and it names only the zones that have a name - so painted ground a GM
 * never got round to naming described itself as nothing at all, however
 * much it was doing. The zone under the point is the subject; what
 * `surfaceAt` returns is what it does.
 *
 * The LAST painted zone is described, on a map from before painting
 * carved (v8.310.8): it is the one whose traits `surfaceAt` lets win, so
 * the description and the effect agree.
 */
function groundLines(at: Vec3, options: InspectOptions): InspectLine[] {
  const here = zonesUnder(at, options);
  if (!here.length) return [];
  const surface = surfaceAt(at.x, at.z, options.zones, options.rules ?? ON_FOOT, options.cellSize ?? 1);
  const zone = here[here.length - 1];
  const facts = zoneFacts(zone, surface.coverAp);
  return [
    {
      title:
        here
          .map((z) => z.label)
          .filter(Boolean)
          .join(', ') || 'Painted ground',
      facts: facts.length ? facts : ['Ordinary ground'],
      meaning: surfaceMeaning(surface),
      detail: options.detail ? zoneDetail(zone) : [],
    },
  ];
}

/** What the painted zone is: its going, its hazard, the cover it gives. */
function zoneFacts(zone: Zone, coverAp: number): string[] {
  const facts: string[] = [];
  if (zone.going && zone.going !== 'normal') facts.push(zone.going === 'impassable' ? 'Impassable' : 'Rough going');
  if (zone.hazard) facts.push('Hazardous');
  if (coverAp) facts.push(`Cover AP ${coverAp}`);
  return facts;
}

/**
 * What the ground grants whoever stands on it.
 *
 * DECODED, not dumped. `Pen:movement=-15` is the storage format; what a
 * GM wants to read is "Movement −15".
 */
function surfaceMeaning(surface: ReturnType<typeof surfaceAt>): string[] {
  const granted = surface.grants.map((code) => decodeCond(code) || code);
  const traits = Object.entries(surface.traits)
    .filter(([, value]) => value)
    .map(([trait, value]) => {
      const label = TRAIT_LABELS[trait] ?? sentence(trait);
      return typeof value === 'number' ? `Grants ${label} ${value}` : `Grants ${label}`;
    });
  return [...granted, ...traits];
}

/** The zone's structural facts, behind the detail switch. */
function zoneDetail(zone: Zone): string[] {
  const deep: string[] = [];
  if (zone.coverLevel && zone.coverLevel !== 'none')
    deep.push(`Cover: ${COVER_WORDS[zone.coverLevel] ?? zone.coverLevel} · AP ${zone.coverAp ?? 0}`);
  const reaches = Object.entries(zone.affects ?? {})
    .filter(([, v]) => v)
    .map(([k]) => REACH_WORDS[k] ?? k);
  deep.push(reaches.length ? `Also reaches ${reaches.join(', ')}` : 'Flyers and burrowers pass untouched');
  if (zone.hidden) deep.push('GM only - players cannot see it');
  return deep;
}

/**
 * Something you are inside that is not a structure or a surface -
 * smoke, a forest - is still worth saying.
 */
function insideLines(at: Vec3, { solids, includeHidden = true }: InspectOptions): InspectLine[] {
  const overlap = overlapAt(at, solids, includeHidden);
  if (!overlap.ground.length) return [];
  return [{ title: overlap.ground.join(', '), facts: ['Ground'], meaning: [], detail: [] }];
}
